import React, { useEffect, useRef, useState, useMemo } from 'react';
import { APIKey, FormSectionElements, LOVValues, ProgramContainer } from '../../entities/formTemplateDataType';
import FormValidationUtils from "../../utilities/FormValidationUtils";
import { getRootComponent } from '../../utilities/ElementUtils';
import CMAUtils from '../../utilities/CMAUtils';
import axios from 'axios';
import SessionStorageService from '../../services/SessionStorageService';
import { formatDateCustom, formatDateWithCustomFormat } from '../../utilities/DateUtils';
import URLUtils from '../../utilities/URLUtils';
import { TemplateStatus } from '../../entities/Constants';
import { colour } from '../../theme';
import { ErrorAlertComponent } from '../odsCalloutBox/ErrorAlertComponent';
import OerUtils from '../../utilities/custom/OerUtils';
import { setBaseHeaders } from '../../utilities/submitUtils';
import "primeicons/primeicons.css";
import { handleAzure401 } from '../../security/AzureAuthService';

export type TableProps = {
    isEng: boolean;
    field: FormSectionElements;
    callbackStepIndex?: (index: number, returnIndex?: number) => void;
    callback?: (programContainer: ProgramContainer) => void;
};

type SortDirection = 'asc' | 'desc' | 'none';

const allowEnter = (userRole: string, status: string) => {
    status = status.replace(" ", "");

    if (status === TemplateStatus.CANCELED)
        return false;

    if (userRole === "Assessor") return true;
    if (
        userRole === "Approver" &&
        [TemplateStatus.APPROVAL_PENDING, TemplateStatus.REVISION, TemplateStatus.SUBMITTED, TemplateStatus.COMPLETED].includes(status.split(/\s+/).join(''))
    ) return true;
    if (
        userRole === "CSD Admin" &&
        [TemplateStatus.SUBMITTED, TemplateStatus.COMPLETED].includes(status.split(/\s+/).join(''))
    ) return true;
    return false;
};

const SummaryListComponent = ({ field, isEng, callbackStepIndex, callback }: TableProps) => {
    const [data, setData] = useState<any[]>([]);
    const [isLoading, setIsLoading] = useState<boolean>(true);
    const [errorVisible, setErrorVisible] = useState<boolean>(false);
    const [defaultData, setDefaultData] = useState<any[]>([]);
    const [sortColumn, setSortColumn] = useState<string | null>(null);
    const [sortDirection, setSortDirection] = useState<SortDirection>('none');
    const [hasMounted, setHasMounted] = useState<boolean>(false);
    const [selectedRowIndex, setSelectedRowIndex] = useState<number | null>(null);
    const scriptDataRef = useRef<any[] | null>(null);

    const getTableKey = () => {
        return String(field.LoadBindName || field.Id || field.ENUTitle || 'summaryList');
    };

    const setSelectedRowData = (record: any) => {
        const programContainer = getRootComponent(field) as any;
        if (!programContainer) return;
        const tableKey = getTableKey();
        if (!programContainer.selectedRowDataByTable) {
            programContainer.selectedRowDataByTable = {};
        }
        programContainer.selectedRowDataByTable[tableKey] = record;
        programContainer.selectedRowData = record;
    };

    // Handle row selection via radio button
    const handleRowSelect = (record: any, idx: number) => {
        setSelectedRowIndex(idx);
        setSelectedRowData(record);
    };

    const elements = field.subSectionList[0].ListOfFormSectionElements;

    const collectSummaryFilters = (): { columns: string[]; value: string }[] => {
        const filters: { columns: string[]; value: string }[] = [];
        const traverseElements = (elementList?: FormSectionElements[]) => {
            if (!elementList) return;
            elementList.forEach(element => {
                if (!element) return;
                const typedElement = element as any;
                if ((element as any)?.Visible === false) return;

                if (typedElement?.Datatype === "SearchAutocomplete" || 
                    typedElement?.Datatype === "Date" || 
                    typedElement?.Datatype === "ODSDate" || 
                    typedElement?.Datatype === "ODSDateRange") {
                    const columnSelection = Array.isArray(typedElement.filterSummaryListColumns) && typedElement.filterSummaryListColumns.length
                        ? typedElement.filterSummaryListColumns
                        : (typedElement.filterSummaryListColumn ? [typedElement.filterSummaryListColumn] : []);
                    if (typedElement.filterSummaryListId === field.Id && columnSelection.length) {
                        const selectedValue = element.Value ?? typedElement.SelectionCode;
                        if (selectedValue !== null && selectedValue !== undefined && `${selectedValue}` !== "") {
                            filters.push({
                                columns: columnSelection,
                                value: String(selectedValue)
                            });
                        }
                    }
                }
                if (element.subSectionList?.length) {
                    element.subSectionList.forEach(sub => traverseElements(sub?.ListOfFormSectionElements));
                }
            });
        };

        traverseElements(field.parent?.ListOfFormSectionElements);

        const rootSections = getRootComponent(field)?.ListOfFormSections ?? [];
        rootSections.forEach(section => {
            section?.ListOfFormSubSections?.forEach(subSection => {
                traverseElements(subSection?.ListOfFormSectionElements);
            });
        });

        return filters;
    };

    const updateLinkedSearchAutocompleteOptions = (records: any[]) => {
        if (!Array.isArray(records)) return;
        const programContainer = getRootComponent(field);
        const sections = programContainer?.ListOfFormSections ?? [];

        const registerOptionsForElement = (searchElement: FormSectionElements) => {
            const searchConfig = searchElement as any;
            const columnNames: string[] = Array.isArray(searchConfig?.filterSummaryListColumns) && searchConfig?.filterSummaryListColumns.length
                ? searchConfig.filterSummaryListColumns
                : (searchConfig?.filterSummaryListColumn ? [searchConfig.filterSummaryListColumn] : []);
            if (!columnNames.length) {
                return;
            }

            const optionsMap = new Map<string, LOVValues>();
            const pushValue = (raw: any) => {
                if (raw === null || raw === undefined) return;
                const stringValue = String(raw).trim();
                if (!stringValue) return;
                if (!optionsMap.has(stringValue)) {
                    const lov = new LOVValues();
                    lov.code = stringValue;
                    lov.description = stringValue;
                    lov.FRADescription = stringValue;
                    lov.parent = searchElement;
                    optionsMap.set(stringValue, lov);
                }
            };

            records.forEach(record => {
                columnNames.forEach(columnName => {
                    const rawValue = record?.[columnName];
                    if (Array.isArray(rawValue)) {
                        rawValue.forEach(val => pushValue(val));
                    } else {
                        pushValue(rawValue);
                    }
                });
            });

            const newOptions = Array.from(optionsMap.values());
            searchElement.ListOfLOVValues = newOptions;
        };

        const traverseElements = (elements?: FormSectionElements[]) => {
            if (!elements) return;
            elements.forEach(element => {
                if (!element) return;
                if ((element as any)?.filterSummaryListId === field.Id && (
                    element.Datatype === 'SearchAutocomplete' || 
                    element.Datatype === 'Date' || 
                    element.Datatype === 'ODSDate' || 
                    element.Datatype === 'ODSDateRange'
                )) {
                    registerOptionsForElement(element);
                }
                if (element.subSectionList?.length) {
                    element.subSectionList.forEach(sub => traverseElements(sub?.ListOfFormSectionElements));
                }
            });
        };

        traverseElements(field.parent?.ListOfFormSectionElements);

        sections.forEach(section => {
            section?.ListOfFormSubSections?.forEach(subSection => {
                traverseElements(subSection?.ListOfFormSectionElements);
            });
        });
    };

    useEffect(() => {
        setHasMounted(true);
    }, []);

    const getClickedRowData = (): any | null => {
        const programContainer = getRootComponent(field) as any;
        const tableKey = getTableKey();
        return programContainer?.clickedRowDataByTable?.[tableKey] ?? programContainer?.clickedRowData ?? null;
    };

    useEffect(() => {
        const getListDataSource = async () => {
            const programContainer = getRootComponent(field);

            if (field.UseClickedRowDataForSummaryList) {
                const clickedRowData = getClickedRowData();
                const clickedData = field.LoadBindName ? clickedRowData?.[field.LoadBindName] : clickedRowData;
                if (clickedData && Array.isArray(clickedData) && clickedData.length > 0) {
                    updateLinkedSearchAutocompleteOptions(clickedData);
                    setData(clickedData);
                    setDefaultData([...clickedData]);
                    setIsLoading(false);
                    return;
                }
            }

            if (field.LoadBindName && programContainer?.Entity) {
                const entityData = programContainer.Entity[field.LoadBindName];
                if (entityData && Array.isArray(entityData) && entityData.length > 0) {
                    updateLinkedSearchAutocompleteOptions(entityData);
                    setData(entityData);
                    setDefaultData([...entityData]);
                    setIsLoading(false);
                    return;
                }
            }

            const apiKey: APIKey = (programContainer.ApiKeyKey && programContainer.ApiKeyValue) ? { key: programContainer.ApiKeyKey, value: programContainer.ApiKeyValue } : null;
            const authToken = SessionStorageService.getAuthToken(programContainer.AuthRequired) ?? programContainer.Profile?.token;
            const headers = setBaseHeaders('application/json', authToken, apiKey);
            const configs = {
                ...{ headers },  // Headers
                withCredentials: true
            }

            let api = "";
            if (field.LovAPI) {
                if (field.LovAPI?.startsWith("http")){ 
                    api = field.LovAPI;
                } else {
                    api = programContainer.APIBasePath?.replace(/\/+$/,'') + '/' + field.LovAPI.replace(/^\/+/,'');
                }
        
                try {
                    const response = await axios.get(api, configs);
                    if (Array.isArray(response?.data)) {
                        updateLinkedSearchAutocompleteOptions(response.data);
                        setData(response.data);
                        setDefaultData([...response.data]);
                    }
                    else {
                        updateLinkedSearchAutocompleteOptions([]);
                        setData([]);
                        setDefaultData([]);
                    }
                } catch (error: any) {
                    if (error.response?.status === 401 || error.response?.status === 403) {
                        if (programContainer.AuthRequired === "ad" && programContainer.CategoryId === "CMA") {
                            try {
                                await handleAzure401();
                                console.info("Azure re-auth succeeded");
                                // Optionally retry the request here
                                return;
                            } catch (e) {
                                console.error("Azure re-auth failed", e);
                                //return;
                            }
                        }
                    }

                    setErrorVisible(true);
                    console.error("Error fetching data:", error);
                } finally {
                    setIsLoading(false);
                }      
            } else {
                updateLinkedSearchAutocompleteOptions([]);
                setData([]);
                setDefaultData([]);
                setIsLoading(false);
            }        
        };

        getListDataSource();
    }, [field]);

    // sync generated data after render.
    useEffect(() => {
        if (scriptDataRef.current && scriptDataRef.current.length > 0) {
            const sd = scriptDataRef.current;
            if (sd.length !== data.length) {
                updateLinkedSearchAutocompleteOptions(sd);
                setData(sd);
                setDefaultData([...sd]);
                setIsLoading(false);
            }
            scriptDataRef.current = null;
        }
    });

    const handleSort = (columnId: string, columnLoadBindName: string) => {
        const sortKey = columnId || columnLoadBindName;
        if (sortColumn === sortKey) {
            if (sortDirection === 'none') {
                setSortDirection('asc');
            } else if (sortDirection === 'asc') {
                setSortDirection('desc');
            } else if (sortDirection === 'desc') {
                setSortDirection('none');
                setSortColumn(null);
            }
        } else {
            setSortColumn(sortKey);
            setSortDirection('asc');
        }
    };

    const activeFilters = collectSummaryFilters();
    
    const sortedData = useMemo(() => {
        const matchesFilter = (record: any, filter: { columns: string[]; value: string }) => {
            if (filter.value.includes('|')) {
                const parts = filter.value.split('|');
                const startDateStr = parts[0] || '';
                const endDateStr = parts[1] || '';
                const startDate = startDateStr ? new Date(startDateStr) : null;
                const endDate = endDateStr ? new Date(endDateStr) : null;
                if (startDate || endDate) {
                    return filter.columns.some(columnName => {
                        const recordValue = record?.[columnName];
                        if (recordValue === null || recordValue === undefined) return false;

                        const recordDate = new Date(recordValue);
                        if (isNaN(recordDate.getTime())) return false;
                        if (startDate && endDate) {
                            return recordDate >= startDate && recordDate <= endDate;
                        } else if (startDate) {
                            return recordDate >= startDate;
                        } else if (endDate) {
                            return recordDate <= endDate;
                        }
                        return false;
                    });
                }
            }

            const normalizedFilter = filter.value.toString().toLowerCase();
            return filter.columns.some(columnName => {
                const recordValue = record?.[columnName];
                if (recordValue === undefined || recordValue === null) {
                    return false;
                }
                if (Array.isArray(recordValue)) {
                    return recordValue.some(item => String(item).toLowerCase() === normalizedFilter);
                }
                return String(recordValue).toLowerCase() === normalizedFilter;
            });
        };

        const filteredData = activeFilters.length
            ? data.filter(record =>
                activeFilters.every(filter => matchesFilter(record, filter))
            )
            : data;
        
        if (sortDirection === 'none' || !sortColumn) {
            return filteredData;
        }

        const column = elements.find(ele => ele.Id === sortColumn || ele.LoadBindName === sortColumn);
        
        if (!column || !column.LoadBindName) {
            return filteredData;
        }

        const sorted = [...filteredData].sort((a, b) => {
            const aValue = a[column.LoadBindName];
            const bValue = b[column.LoadBindName];

            if (aValue === null || aValue === undefined) return 1;
            if (bValue === null || bValue === undefined) return -1;

            let comparison = 0;
            if (typeof aValue === 'number' && typeof bValue === 'number') {
                comparison = aValue - bValue;
            } else {
                comparison = String(aValue).localeCompare(String(bValue), undefined, { numeric: true, sensitivity: 'base' });
            }

            return sortDirection === 'asc' ? comparison : -comparison;
        });
        
        return sorted;
    }, [data, activeFilters, sortColumn, sortDirection]);

    const handleViewEvent = ((column: FormSectionElements, event: React.MouseEvent, data: any) => {
        const functions = {
            getRootComponent: ((column: FormSectionElements) => getRootComponent(column)),
            retrieveFormData: ((programContainer: ProgramContainer, data: any) => OerUtils.retrieveFormData(programContainer, data)),
            callbackStepIndex: ((index: number) => {callbackStepIndex?.(index);}),
        }
        if (column.EventScript) {
            event.preventDefault();
            const customJSFunc = eval(`(${column.EventScript})`);
            customJSFunc(column, data, functions);
        }
    });

    FormValidationUtils.runJavascript(field);

    // capture generated data during render.
    if (field.Script && field.LoadBindName) {
        const clickedRowData = getClickedRowData();
        const scriptData = clickedRowData?.[field.LoadBindName];
        if (scriptData && Array.isArray(scriptData) && scriptData.length > 0) {
            scriptDataRef.current = scriptData;
        }
    }

    if (isLoading && hasMounted) return <p>Loading...</p>;

    const caption = isEng ? field.ENUTitle : field.FRATitle;
    const HtmlToReactParser = require('html-to-react').Parser;
    const htmlToReactParser = new HtmlToReactParser();

    return (
        <>
            {errorVisible && (
                <ErrorAlertComponent
                    bodyMsg="You currently do not have permission to view any assigned CMA assessments. Kindly reach out to the Cyber Security Service Engagement (csse@ontario.ca) for requesting access."
                    title="Insufficient Permission"
                    isEng={true}
                    Visible={true}
                />
            )}

            
            <div className="ontario-table-container ontario-small-12 ontario-medium-12 ontario-large-12">
                <div className="ontario-table-scroll--wrapper">
                    <div className="ontario-table-scroll--div"></div>
                </div>
                <div className="ontario-table-div">
                    <table tabIndex={0} style={{ width: '100%' }}>
                        <caption>{caption}</caption>
                        <thead>
                            <tr>
                                {elements.map(ele => {
                                    if (ele.Datatype === "RowSelector") {
                                        return <th key={ele.Id || ele.LoadBindName || ele.Datatype} scope="col"></th>;
                                    }
                                    if (ele.Datatype === "Button" || ele.Datatype === "CMAButton") {
                                        return <th key={ele.Id || ele.LoadBindName || ele.Datatype} scope="col">{isEng ? ele.ENUTitle : ele.FRATitle}</th>;
                                    }
                                    const isSortable = ele.LoadBindName && ele.Datatype !== "CMAKey" && ele.AllowSorting === true;
                                    const isCurrentSortColumn = sortColumn === ele.Id || sortColumn === ele.LoadBindName;
                                    const getSortIcon = () => {
                                        if (!isSortable) return null;
                                        if (!isCurrentSortColumn) {
                                            return <i className="pi pi-sort" style={{ marginLeft: '5px', opacity: 0.5, cursor: 'pointer' }} />;
                                        }
                                        if (sortDirection === 'asc') {
                                            return <i className="pi pi-sort-up" style={{ marginLeft: '5px', cursor: 'pointer' }} />;
                                        }
                                        if (sortDirection === 'desc') {
                                            return <i className="pi pi-sort-down" style={{ marginLeft: '5px', cursor: 'pointer' }} />;
                                        }
                                        return <i className="pi pi-sort" style={{ marginLeft: '5px', opacity: 0.5, cursor: 'pointer' }} />;
                                    };
                                    return (
                                        <th 
                                            key={ele.Id || ele.LoadBindName} 
                                            scope="col"
                                            style={isSortable ? { cursor: 'pointer', userSelect: 'none' } : {}}
                                            onClick={() => isSortable && handleSort(ele.Id, ele.LoadBindName)}
                                        >
                                            {isEng ? ele.ENUTitle : ele.FRATitle}
                                            {getSortIcon()}
                                        </th>
                                    );
                                })}
                            </tr>
                        </thead>
                        <tbody>
                            {sortedData.length > 0 ? (
                                sortedData.map((record, idx) => {
                                    return (
                                        <tr key={idx}>
                                            {elements.map((column, colIdx) => {
                                                switch (column.Datatype) {
                                                    case "RowSelector":
                                                        return (
                                                            <td key={colIdx} style={{ textAlign: 'center', width: '50px' }}>
                                                                <input
                                                                    type="radio"
                                                                    name={`row-selector-${field.Id}`}
                                                                    checked={selectedRowIndex === idx}
                                                                    onChange={() => handleRowSelect(record, idx)}
                                                                    style={{ cursor: 'pointer', width: '18px', height: '18px' }}
                                                                    aria-label={isEng ? `Select row ${idx + 1}` : `Sélectionner la ligne ${idx + 1}`}
                                                                />
                                                            </td>
                                                        );
                                                    case "CMAKey":
                                                        return (
                                                            <th key={colIdx} scope="row">
                                                                <a
                                                                    href="#"
                                                                    data-status={record.status}
                                                                    style={!allowEnter(record.userRole, record.status) ? { color: colour.hintTextGrey } : {}}
                                                                    onClick={async (event) => {
                                                                        event.preventDefault();
                                                                        if (!allowEnter(record.userRole, record.status)) return;
                                                                        const programContainer = getRootComponent(field);
                                                                        programContainer.Template = record[column.LoadBindName];
                                                                        programContainer.orgId = record.orgId;
                                                                        programContainer.userRole = record.userRole;
                                                                        programContainer.organizationName = record.organizationName;
                                                                        programContainer.formDataId = record.id;
                                                                        CMAUtils.generateCMASection(programContainer);
                                                                        await CMAUtils.retrieveFormData(programContainer, record.status);
                                                                        callbackStepIndex?.(3);
                                                                    }}
                                                                >
                                                                    <div dangerouslySetInnerHTML={{ __html: record.formattedTemplateAssessment }} />
                                                                </a>
                                                            </th>
                                                        );
                                                    case "CMAButton":
                                                        return (
                                                            <td key={colIdx}>
                                                                <button
                                                                    className="ontario-button ontario-button--secondary"
                                                                    onClick={() => {
                                                                        const programContainer = getRootComponent(field);
                                                                        programContainer.Template = record[column.LoadBindName];
                                                                        CMAUtils.generateCMASection(programContainer);
                                                                        callbackStepIndex?.(3);
                                                                    }}
                                                                >
                                                                    {column.ENUTitle}
                                                                </button>
                                                            </td>
                                                        );
                                                    case "Text":
                                                        let textToDisplay = record[column.LoadBindName];
                                                        if (!textToDisplay?.trim().length) {
                                                            textToDisplay = isEng ? (column.ToDisplayWhenEmpty ? column.ToDisplayWhenEmpty : '') : (column.ToDisplayWhenEmptyFRA ? column.ToDisplayWhenEmptyFRA : '');
                                                        };

                                                        // translate CMA status
                                                        if (!isEng && column.LoadBindName === "status") {
                                                            switch (textToDisplay) {
                                                                case "Approval Pending":
                                                                    textToDisplay = "En attente d’approbation";
                                                                    break;
                                                                case "Assigned":
                                                                    textToDisplay = "Attribuée";
                                                                    break;
                                                                case "In Progress":
                                                                    textToDisplay = "En cours";
                                                                    break;
                                                                case "Submitted":
                                                                    textToDisplay = "Envoyée";
                                                                    break;
                                                                case "Revision":
                                                                    textToDisplay = "Révision";
                                                                    break;
                                                                case "Completed":
                                                                    textToDisplay = "Terminée";
                                                                    break;
                                                                case "Canceled":
                                                                    textToDisplay = "Annulé";
                                                                    break;
                                                            }
                                                        }
                                                            // translate CMA status
                                                        if (!isEng && column.LoadBindName === "userRole") {
                                                            switch (textToDisplay) {
                                                                case "Assessor":
                                                                    textToDisplay = "Responsable de l’évaluation";
                                                                    break;
                                                                case "Approver":
                                                                    textToDisplay = "Responsable de l’approbation";
                                                                    break;
                                                            }
                                                        }
                                                        return <td key={colIdx}>{textToDisplay}</td>;
                                                    case "Date":
                                                        let dateToDisplay = '';
                                                        if (record[column.LoadBindName]?.trim().length) {
                                                            dateToDisplay = column.DateDisplayFormat ? formatDateWithCustomFormat(record[column.LoadBindName], column.DateDisplayFormat) : formatDateCustom(record[column.LoadBindName]);
                                                        };
                                                        if(!dateToDisplay || dateToDisplay === ""|| dateToDisplay === "0001-01-01" || dateToDisplay === "0001-01-01 00:00:00") {
                                                            dateToDisplay = isEng ? column.ToDisplayWhenEmpty :column.ToDisplayWhenEmptyFRA;
                                                        }
                                                        return (
                                                            <td key={colIdx}>
                                                                {dateToDisplay}
                                                            </td>
                                                        );
                                                    case "Button":
                                                        return (
                                                            <th key={colIdx} scope="row">
                                                                <a
                                                                    href="#"
                                                                    data-data={sortedData}
                                                                    onClick={(event) => handleViewEvent(column, event, record)}>
                                                                    {isEng ? (column.ButtonLabel || column.ENUTitle) : (column.ButtonLabelFRA || column.FRATitle)}
                                                                </a>
                                                            </th>
                                                        );
                                                    default:
                                                        return <td key={colIdx}></td>;
                                                }
                                            })}
                                        </tr>
                                    );
                                })
                            ) : (
                                <tr>
                                    <td colSpan={elements.length} style={{ textAlign: 'center', padding: '20px' }}>
                                        {htmlToReactParser.parse(isEng ? field.TableEmptyMessage || "No records found" : field.TableEmptyMessageFRA || "Aucun enregistrement trouvé.")}
                                    </td>
                                </tr>
                            )}
                        </tbody>
                    </table>
                </div>
            </div>
        </>
    );
};

export default SummaryListComponent;
